import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { useRealtime } from '../lib/realtime';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeader } from '../components/common/PageHeader';
import { StatusPill } from '../components/common/StatusPill';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { OrderDrawer } from '../components/common/OrderDrawer';
import { requestBillAndPrint } from '../lib/receipt';
import { prefetchMenu } from '../lib/menuCache';
import { TABLE_STATUS, TABLE_STATUS_LEGEND, tableStatusLabel } from '../lib/constants';
import { useConfirm } from '../context/ConfirmContext';
import { cn } from '../lib/utils';
import {
  Plus, Move, Users2, Receipt, ArrowRightLeft, Trash2, Pencil, Combine,
  X, LayoutGrid, Map as MapIcon, Maximize2, SlidersHorizontal, ChevronDown, Check,
} from 'lucide-react';


export default function TablesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [zones, setZones] = useState([]);
  const [activeZone, setActiveZone] = useState(null);
  const [viewMode, setViewMode] = useState('map'); // 'map' | 'grid'
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const confirm = useConfirm();
  const [editMode, setEditMode] = useState(false);
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSel, setMergeSel] = useState([]);
  const [deleteMode, setDeleteMode] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: '', seats: 4, shape: 'square', zone_id: null });
  const [editing, setEditing] = useState(null);
  const [changeOpen, setChangeOpen] = useState(false);
  const [zoneDialog, setZoneDialog] = useState(false);
  const [zoneForm, setZoneForm] = useState({ name: '', width: 920, height: 600 });
  const [editingZone, setEditingZone] = useState(null);

  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0 });

  const loadZones = async () => {
    const { data } = await api.get('/zones');
    setZones(data);
    setActiveZone((prev) => (prev && data.find((z) => z.id === prev)) ? prev : (data[0]?.id || null));
  };
  const loadTables = async () => {
    const { data } = await api.get('/tables');
    setTables(data);
    setSelected((prev) => prev ? data.find((t) => t.id === prev.id) || null : null);
  };
  const load = async () => { await Promise.all([loadZones(), loadTables()]); };

  useEffect(() => { load(); prefetchMenu('tavoli'); const t = setInterval(loadTables, 12000); return () => clearInterval(t); }, []);

  useRealtime((msg) => {
    if (msg && (msg.event === 'tables' || msg.event === 'orders' || msg.event === 'payments')) loadTables();
  });

  // auto-fit observer
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [viewMode, activeZone]);

  const zone = useMemo(() => zones.find((z) => z.id === activeZone), [zones, activeZone]);
  const inActiveZone = (t) => t.zone_id === activeZone || (!t.zone_id && activeZone === zones[0]?.id);
  const zoneTables = useMemo(() => tables.filter((t) => !t.merged_hidden && t.placed !== false && inActiveZone(t)), [tables, activeZone, zones]);
  const unplacedTables = useMemo(() => tables.filter((t) => !t.merged_hidden && t.placed === false && inActiveZone(t)).sort((a, b) => (a.number || 0) - (b.number || 0)), [tables, activeZone, zones]);
  const scale = (zone && box.w) ? Math.min(box.w / (zone.width + 40), box.h / (zone.height + 40)) : 1;

  const selectTile = (t) => {
    if (mergeMode) { setMergeSel((s) => s.includes(t.id) ? s.filter((x) => x !== t.id) : [...s, t.id]); return; }
    if (deleteMode) { removeTable(t); return; }
    // Normal mode: open the table directly in the right-side drawer
    setSelected(t);
    setDrawerOpen(true);
  };

  const openOrder = async (tbl) => {
    try {
      if (tbl.status === 'free') await api.post('/orders/open', { table_id: tbl.id });
      navigate(`/ordine/${tbl.id}`);
    } catch (e) { toast.error(t('tables.openTableError')); }
  };

  // drag (map only) — usa Pointer Events per supportare mouse E touch (touchscreen).
  const onTileDown = (e, t) => {
    if (!editMode || viewMode !== 'map') return;
    e.preventDefault(); e.stopPropagation();
    dragRef.current = { id: t.id, pointerId: e.pointerId };
    // Cattura il puntatore sul canvas così i movimenti (anche fuori dal tile o col
    // dito) continuano ad arrivare ai gestori pointermove/up.
    try { wrapRef.current?.setPointerCapture?.(e.pointerId); } catch (err) {}
  };
  const onMove = (e) => {
    if (resizeRef.current && zone) {
      const dx = (e.clientX - resizeRef.current.sx) / scale;
      const dy = (e.clientY - resizeRef.current.sy) / scale;
      const w = Math.round(Math.max(400, resizeRef.current.w0 + dx) / 10) * 10;
      const h = Math.round(Math.max(300, resizeRef.current.h0 + dy) / 10) * 10;
      setZones((zs) => zs.map((z) => z.id === zone.id ? { ...z, width: w, height: h } : z));
      return;
    }
    if (!dragRef.current || !canvasRef.current) return;
    // Cattura l'id PRIMA di setTables: l'updater viene eseguito da React durante
    // il render e, se il drag termina nel frattempo (onUp azzera dragRef), leggere
    // dragRef.current.id causerebbe un crash "Cannot read properties of null".
    const dragId = dragRef.current.id;
    const rect = canvasRef.current.getBoundingClientRect();
    let x = Math.round(((e.clientX - rect.left) / scale - 45) / 10) * 10;
    let y = Math.round(((e.clientY - rect.top) / scale - 45) / 10) * 10;
    x = Math.max(0, Math.min(x, (zone?.width || 900) - 90));
    y = Math.max(0, Math.min(y, (zone?.height || 600) - 90));
    setTables((prev) => prev.map((t) => t.id === dragId ? { ...t, x, y } : t));
  };
  const onUp = async () => {
    if (resizeRef.current && zone) {
      resizeRef.current = null;
      const z = zones.find((x) => x.id === zone.id);
      if (z) { try { await api.put(`/zones/${z.id}`, { name: z.name, width: z.width, height: z.height }); } catch (e) {} }
      return;
    }
    if (!dragRef.current) return;
    const t = tables.find((x) => x.id === dragRef.current.id);
    const id = dragRef.current.id;
    const pid = dragRef.current.pointerId;
    dragRef.current = null;
    try { if (pid != null) wrapRef.current?.releasePointerCapture?.(pid); } catch (err) {}
    if (t) { try { await api.patch(`/tables/${id}/position`, { x: t.x, y: t.y, zone_id: activeZone }); } catch (e) {} }
  };
  const onResizeDown = (e) => {
    if (!zone) return;
    e.preventDefault(); e.stopPropagation();
    resizeRef.current = { sx: e.clientX, sy: e.clientY, w0: zone.width, h0: zone.height };
    try { wrapRef.current?.setPointerCapture?.(e.pointerId); } catch (err) {}
  };

  const saveTable = async () => {
    if (!form.name || !form.name.trim()) { toast.error(t('tables.enterName')); return; }
    try {
      if (editing) { await api.put(`/tables/${editing.id}`, { ...editing, ...form, name: form.name.trim() }); toast.success(t('tables.tableUpdated')); }
      else { await api.post('/tables', { ...form, name: form.name.trim(), zone_id: form.zone_id || activeZone, x: 40, y: 40 }); toast.success(t('tables.tableCreated')); }
      setAddOpen(false); setEditing(null); setForm({ name: '', seats: 4, shape: 'square', zone_id: activeZone }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('tables.saveError')); }
  };

  // Place an existing (grid) table onto the current room's map.
  const placeTable = async (tbl) => {
    // Drop it at a free-ish spot near the top-left, snapped to grid.
    const used = zoneTables.map((z) => `${z.x},${z.y}`);
    let x = 40, y = 40;
    for (let i = 0; i < 60 && used.includes(`${x},${y}`); i++) {
      x += 110; if (x > (zone?.width || 900) - 100) { x = 40; y += 110; }
    }
    try {
      await api.patch(`/tables/${tbl.id}/place`, { x, y, zone_id: activeZone });
      toast.success(t('tables.placedOnMap'));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('tables.genericError')); }
  };
  const unplaceTable = async (tbl) => {
    try {
      await api.patch(`/tables/${tbl.id}/unplace`);
      toast.success(t('tables.removedFromMap'));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('tables.genericError')); }
  };
  const removeTable = async (tbl) => {
    if (tbl.merged || tbl.group_id) { toast.error(t('tables.mergedSeparateFirst')); return; }
    if (!(await confirm({ title: t('tables.deleteConfirm', { name: tbl.name }), destructive: true, confirmText: t('common.delete') }))) return;
    try {
      await api.delete(`/tables/${tbl.id}`);
      if (selected?.id === tbl.id) setSelected(null);
      load();
      toast.success(t('tables.tableDeleted'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('tables.deleteError'));
    }
  };

  const requestBill = async (tbl) => {
    const { data } = await api.get(`/orders/by-table/${tbl.id}`).catch(() => ({ data: null }));
    if (!data) { toast.error(t('tables.noActiveOrder')); return; }
    try {
      const via = await requestBillAndPrint(api, data.id);
      toast.success(via === 'agent' ? t('order.billSentToPrinter') : t('order.billPrinting'));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('order.billError'));
    }
  };
  const doMerge = async () => {
    if (mergeSel.length < 2) { toast.error(t('tables.selectAtLeast2')); return; }
    try {
      await api.post('/tables/merge', { table_ids: mergeSel });
      toast.success(t('tables.tablesMerged'));
      setMergeMode(false); setMergeSel([]); setSelected(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('tables.mergeError')); }
  };
  const unmerge = async (tbl) => {
    try { await api.post(`/tables/${tbl.id}/unmerge`); toast.success(t('tables.tablesSeparated')); setSelected(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('tables.genericError')); }
  };
  const changeTable = async (targetId) => { if (!selected?.id) return; try { await api.post(`/tables/${selected.id}/change`, { target_table_id: targetId }); toast.success(t('tables.tableChanged')); setChangeOpen(false); load(); } catch (e) { toast.error(e?.response?.data?.detail || t('tables.genericError')); } };

  // zones
  const saveZone = async () => {
    if (!zoneForm.name.trim()) { toast.error(t('tables.roomNameRequired')); return; }
    try {
      if (editingZone) { await api.put(`/zones/${editingZone.id}`, zoneForm); toast.success(t('tables.roomUpdated')); }
      else { const { data } = await api.post('/zones', zoneForm); toast.success(t('tables.roomCreated')); setActiveZone(data.id); }
      setZoneDialog(false); setEditingZone(null); loadZones();
    } catch (e) { toast.error(e?.response?.data?.detail || t('tables.genericError')); }
  };
  const deleteZone = async (z) => {
    if (!(await confirm({ title: t('tables.deleteRoomConfirm', { name: z.name }), destructive: true, confirmText: t('common.delete') }))) return;
    try { await api.delete(`/zones/${z.id}`); loadZones(); toast.success(t('tables.roomDeleted')); }
    catch (e) { toast.error(e?.response?.data?.detail || t('tables.genericError')); }
  };
  const resizeZone = async (dw, dh) => {
    if (!zone) return;
    const w = Math.max(400, zone.width + dw); const h = Math.max(300, zone.height + dh);
    setZones((zs) => zs.map((z) => z.id === zone.id ? { ...z, width: w, height: h } : z));
    try { await api.put(`/zones/${zone.id}`, { name: zone.name, width: w, height: h }); } catch (e) {}
  };

  const freeTables = tables.filter((t) => t.status === 'free' && !t.merged_hidden && !t.merged && t.id !== selected?.id);
  // NOTA: la griglia mostra SOLO i tavoli della sala attiva (una schermata per sala, come nella mappa).
  const gridTables = [...tables]
    .filter((t) => !t.merged_hidden && inActiveZone(t))
    .sort((a, b) => (a.number || 0) - (b.number || 0));

  const TileVisual = ({ t: tbl, sizePx, absolute }) => {
    const st = TABLE_STATUS[tbl.status] || TABLE_STATUS.free;
    const isMergeSel = mergeSel.includes(tbl.id);
    const isMerged = !!tbl.merged;
    const label = isMerged ? tbl.merged_label : (tbl.name || tbl.number);
    const seats = isMerged ? tbl.merged_seats : tbl.seats;
    const isVertical = tbl.merged_orientation === 'vertical';
    // grid sizes reflect orientation (horizontal => wider, vertical => taller)
    const gridW = isMerged && !isVertical ? (tbl.merged_count || 2) * sizePx : sizePx;
    const gridH = isMerged && isVertical ? (tbl.merged_count || 2) * sizePx : sizePx;
    return (
      <div
        data-testid="table-tile"
        onPointerDown={(e) => absolute && onTileDown(e, tbl)}
        onClick={() => !(editMode && absolute) && selectTile(tbl)}
        className={cn('flex flex-col items-center justify-center select-none transition-shadow',
          (editMode && absolute) ? 'cursor-move' : 'cursor-pointer',
          (isMerged || tbl.shape === 'rect') ? 'rounded-2xl' : (tbl.shape === 'round' ? 'rounded-full' : 'rounded-2xl'),
          isMergeSel && 'ring-2 ring-offset-2 ring-offset-surface-2 ring-primary shadow-md',
          deleteMode && 'ring-2 ring-offset-2 ring-offset-surface-2 ring-destructive/50 hover:ring-destructive',
          absolute ? 'absolute' : 'relative')}
        style={{
          ...(absolute ? { left: tbl.x, top: tbl.y, width: tbl.width || 90, height: tbl.height || 90 } : { width: gridW, height: gridH }),
          backgroundColor: `hsl(var(--${st.color}) / 0.12)`,
          border: `2px solid hsl(var(--${st.color}))`,
          touchAction: (editMode && absolute) ? 'none' : undefined,
        }}
      >
        <span className={cn('font-display font-semibold truncate max-w-[92%] px-1 text-center leading-tight', isMerged ? 'text-sm' : 'text-base')} style={{ color: `hsl(var(--${st.color}))` }}>{label}</span>
        <span className="flex items-center gap-1 text-[11px] text-muted-foreground"><Users2 className="h-3 w-3" />{seats}</span>
        {isMerged && <span className="text-[9px] mt-0.5 px-1.5 rounded bg-primary/15 text-primary">{tbl.merged_count} {t('tables.mergedSuffix')}</span>}
        {editMode && absolute && !tbl.merged && !tbl.group_id && (
          <button
            data-testid={`unplace-table-${tbl.id}`}
            title={t('tables.removeFromMap')}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); unplaceTable(tbl); }}
            className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shadow-md hover:scale-110 transition-transform"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 h-full flex flex-col">
      <PageHeader title={t('tables.title')} subtitle={t('tables.subtitle')} spread>
        {/* Schede sala + legenda, sulla stessa riga dei controlli vista (h-9). */}
        {/* Le schede sala vengono mostrate sia in mappa che in griglia: ogni sala ha la sua schermata. */}
        {zones.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            {zones.map((z) => (
              <button key={z.id} onClick={() => setActiveZone(z.id)} data-testid="zone-tab"
                className={cn('h-9 px-4 rounded-lg text-sm font-medium whitespace-nowrap flex items-center gap-2 transition-colors', activeZone === z.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-secondary')}>
                {z.name}
                {activeZone === z.id && (
                  <span className="flex items-center gap-1">
                    <span onClick={(e) => { e.stopPropagation(); setEditingZone(z); setZoneForm({ name: z.name, width: z.width, height: z.height }); setZoneDialog(true); }} className="hover:opacity-70"><Pencil className="h-3.5 w-3.5" /></span>
                    {zones.length > 1 && <span onClick={(e) => { e.stopPropagation(); deleteZone(z); }} className="hover:opacity-70"><X className="h-3.5 w-3.5" /></span>}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        {/* +Sala è un elemento indipendente: viene distanziato equamente dagli altri. */}
        <button data-testid="add-zone-button" onClick={() => { setEditingZone(null); setZoneForm({ name: '', width: 920, height: 600 }); setZoneDialog(true); }}
          className="h-9 px-4 rounded-lg text-sm font-medium whitespace-nowrap flex items-center gap-1.5 border border-border bg-card hover:bg-muted transition-colors shrink-0"><Plus className="h-4 w-4" /> {t('tables.room')}</button>
        {editMode && zone && (
          <div className="flex items-center gap-1 shrink-0 bg-muted rounded-lg px-2 h-9">
            <Maximize2 className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground mr-1">{zone.width}×{zone.height}</span>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => resizeZone(-120, -80)}>−</Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => resizeZone(120, 80)}>+</Button>
          </div>
        )}
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="tables-legend-button"
              aria-label={t('tables.legendTitle')}
              title={t('tables.legendTitle')}
              className="h-9 w-9 shrink-0 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <span
                className="h-6 w-6 rounded-full border border-border shadow-sm"
                style={{ background: 'conic-gradient(hsl(var(--pos-free)) 0 33.34%, hsl(var(--pos-ordered)) 33.34% 66.67%, hsl(var(--pos-bill)) 66.67% 100%)' }}
              />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-56" data-testid="tables-legend-popover">
            <div className="text-sm font-semibold mb-2">{t('tables.legendTitle')}</div>
            <div className="space-y-2">
              {TABLE_STATUS_LEGEND.map((k) => {
                const v = TABLE_STATUS[k];
                return (
                  <div key={k} className="flex items-center gap-2 text-sm" data-testid={`legend-item-${k}`}>
                    <span className={cn('h-3.5 w-3.5 rounded-full shrink-0', v.dot)} />
                    <span>{tableStatusLabel(t, k)}</span>
                  </div>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>

        <div className="flex rounded-lg bg-muted p-1">
          <button onClick={() => setViewMode('grid')} data-testid="view-grid-button" className={cn('px-3 h-9 rounded-md text-sm font-medium flex items-center gap-1.5', viewMode === 'grid' && 'bg-card shadow-sm')}><LayoutGrid className="h-4 w-4" /> {t('tables.grid')}</button>
          <button onClick={() => setViewMode('map')} data-testid="view-map-button" className={cn('px-3 h-9 rounded-md text-sm font-medium flex items-center gap-1.5', viewMode === 'map' && 'bg-card shadow-sm')}><MapIcon className="h-4 w-4" /> {t('tables.map')}</button>
        </div>

        {mergeMode && <Button onClick={doMerge} className="rounded-xl" data-testid="merge-confirm-button">{t('tables.confirmCount', { count: mergeSel.length })}</Button>}
        {mergeMode && <Button variant="outline" onClick={() => { setMergeMode(false); setMergeSel([]); }} className="rounded-xl">{t('common.cancel')}</Button>}
        {deleteMode && <Button variant="outline" onClick={() => setDeleteMode(false)} className="rounded-xl" data-testid="delete-mode-done-button"><Check className="h-4 w-4 mr-2" /> {t('tables.doneDelete')}</Button>}
        {editMode && viewMode === 'map' && <Button variant="outline" onClick={() => setEditMode(false)} className="rounded-xl"><Check className="h-4 w-4 mr-2" /> {t('tables.doneEdit')}</Button>}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="rounded-xl" data-testid="edit-menu-button">
              <SlidersHorizontal className="h-4 w-4 mr-2" /> {t('tables.edit')} <ChevronDown className="h-4 w-4 ml-1.5 opacity-70" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>{t('tables.tableOps')}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="menu-add-table" onClick={() => { setEditing(null); setForm({ name: '', seats: 4, shape: 'square', zone_id: activeZone }); setAddOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" /> {t('tables.addTable')}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="menu-merge-tables" onClick={() => { setMergeMode(true); setMergeSel([]); setEditMode(false); setDeleteMode(false); }}>
              <Combine className="h-4 w-4 mr-2" /> {t('tables.mergeTables')}
              {mergeMode && <Check className="h-4 w-4 ml-auto text-primary" />}
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="menu-move-tables"
              disabled={viewMode !== 'map'}
              onClick={() => { setViewMode('map'); setEditMode(true); setMergeMode(false); setDeleteMode(false); }}
            >
              <Move className="h-4 w-4 mr-2" /> {t('tables.moveTables')}
              {editMode && <Check className="h-4 w-4 ml-auto text-primary" />}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="menu-delete-tables"
              className="text-destructive focus:text-destructive"
              onClick={() => { setDeleteMode(true); setMergeMode(false); setMergeSel([]); setEditMode(false); }}
            >
              <Trash2 className="h-4 w-4 mr-2" /> {t('tables.deleteTable')}
              {deleteMode && <Check className="h-4 w-4 ml-auto" />}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </PageHeader>

      {(mergeMode || editMode || deleteMode) && (
        <div className={cn('mb-4 rounded-xl border px-4 py-2.5 text-sm flex items-center gap-2',
          deleteMode ? 'border-destructive/40 bg-destructive/5 text-destructive' : 'border-primary/40 bg-primary/5 text-primary')}>
          {deleteMode ? <Trash2 className="h-4 w-4" /> : <SlidersHorizontal className="h-4 w-4" />}
          {deleteMode
            ? t('tables.deleteModeHint')
            : mergeMode
              ? t('tables.mergeModeHint')
              : t('tables.moveModeHint')}
        </div>
      )}

      <div className="flex-1 min-h-0">
        <div className="flex flex-col min-h-0 h-full">
          <Card className="p-0 overflow-hidden flex-1 min-h-[420px]">
            {viewMode === 'map' ? (
              <div className="flex h-full min-h-[420px]">
                <div ref={wrapRef} onPointerMove={onMove} onPointerUp={onUp}
                  data-testid="floor-map-canvas"
                  className="relative flex-1 h-full overflow-hidden bg-surface-2"
                  style={{ minHeight: 420, backgroundImage: 'radial-gradient(hsl(var(--border)) 1px, transparent 1px)', backgroundSize: '20px 20px' }}>
                  {/* Tendina a comparsa: mostrata solo se ci sono tavoli da posizionare */}
                  {unplacedTables.length > 0 && (
                  <div className="absolute top-3 right-3 z-20">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button size="icon" variant="secondary" data-testid="unplaced-toggle"
                          title={t('tables.unplacedTitle')}
                          className="relative h-11 w-11 rounded-full shadow-md border border-border bg-card/95 backdrop-blur hover:bg-card">
                          <LayoutGrid className="h-5 w-5 text-primary" />
                          <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-primary text-primary-foreground text-[11px] font-bold shadow">
                            {unplacedTables.length}
                          </span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent align="end" className="w-64 p-0" data-testid="unplaced-popover">
                        <div className="px-3 py-2.5 border-b border-border">
                          <div className="text-sm font-semibold">{t('tables.unplacedTitle')}</div>
                          <div className="text-[11px] text-muted-foreground">{t('tables.unplacedHint')}</div>
                        </div>
                        <div className="max-h-72 overflow-auto p-2 space-y-1.5">
                          {unplacedTables.length === 0 && <div className="text-xs text-muted-foreground text-center py-6 px-2">{t('tables.unplacedEmpty')}</div>}
                          {unplacedTables.map((tbl) => (
                            <button key={tbl.id} data-testid={`place-table-${tbl.id}`} onClick={() => placeTable(tbl)}
                              className="w-full flex items-center justify-between rounded-lg border border-border px-2.5 py-2 text-left hover:border-primary hover:bg-primary/5 transition-colors">
                              <span className="min-w-0">
                                <span className="block text-sm font-medium truncate">{tbl.name}</span>
                                <span className="flex items-center gap-1 text-[11px] text-muted-foreground"><Users2 className="h-3 w-3" />{tbl.seats}</span>
                              </span>
                              <Plus className="h-4 w-4 text-primary shrink-0" />
                            </button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  )}
                  {zone && (
                    <div ref={canvasRef} className="absolute left-1/2 top-1/2 bg-card/60 rounded-xl border border-border shadow-inner"
                      style={{ width: zone.width, height: zone.height, transform: `translate(-50%, -50%) scale(${scale})`, transformOrigin: 'center center' }}>
                      {zoneTables.map((tbl) => <TileVisual key={tbl.id} t={tbl} absolute />)}
                      {zoneTables.length === 0 && <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm px-4 text-center">{t('tables.emptyRoomPlace')}</div>}
                      {editMode && (
                        <div
                          data-testid="room-resize-handle"
                          onPointerDown={onResizeDown}
                          title={t('tables.dragResize')}
                          style={{ touchAction: 'none' }}
                          className="absolute -bottom-1 -right-1 h-7 w-7 rounded-tl-lg bg-primary/80 cursor-nwse-resize flex items-end justify-end p-0.5"
                        >
                          <Maximize2 className="h-4 w-4 text-primary-foreground" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* (Il pannello 'da posizionare' è ora una tendina a comparsa sulla mappa) */}
              </div>
            ) : (
              <div className="p-4 overflow-auto h-full space-y-5">
                {/* Le sale si selezionano/gestiscono dalle schede in alto (header):
                    la sezione duplicata "Salas" è stata rimossa per evitare doppioni. */}
                {/* Tavoli (si creano ed eliminano qui) */}
                <div>
                  <div className="text-sm font-semibold mb-2 flex items-center gap-2"><LayoutGrid className="h-4 w-4 text-muted-foreground" /> {t('tables.tablesSection')}{zone ? ` · ${zone.name}` : ''}</div>
                  <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
                    {gridTables.map((tbl) => <TileVisual key={tbl.id} t={tbl} sizePx={144} />)}
                    {gridTables.length === 0 && <div className="text-muted-foreground py-10 text-center w-full">{t('tables.noTables')}</div>}
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Order drawer (opens directly when a table is clicked) */}
      <OrderDrawer
        open={drawerOpen}
        onOpenChange={(o) => { setDrawerOpen(o); if (!o) load(); }}
        table={selected}
        onChanged={load}
        onRequestChange={(tbl) => { setSelected(tbl); setChangeOpen(true); }}
        onRequestEdit={(tbl) => { setEditing(tbl); setForm({ name: tbl.name, seats: tbl.seats, shape: tbl.shape, zone_id: tbl.zone_id }); setAddOpen(true); }}
      />

      {/* Add/Edit table */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? t('tables.editTable') : t('tables.newTable')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>{t('tables.tableName')}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t('tables.tableNamePlaceholder')} className="mt-1.5" data-testid="table-name-input" /></div>
            <div><Label>{t('tables.seats')}</Label><Input type="number" value={form.seats} onChange={(e) => setForm({ ...form, seats: Number(e.target.value) })} className="mt-1.5" /></div>
            <div>
              <Label>{t('tables.shape')}</Label>
              <Select value={form.shape} onValueChange={(v) => setForm({ ...form, shape: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="square">{t('tables.square')}</SelectItem><SelectItem value="round">{t('tables.round')}</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAddOpen(false)}>{t('common.cancel')}</Button><Button onClick={saveTable} data-testid="table-save-button">{t('common.save')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zone dialog */}
      <Dialog open={zoneDialog} onOpenChange={setZoneDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingZone ? t('tables.editRoom') : t('tables.newRoom')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t('tables.roomName')}</Label><Input value={zoneForm.name} onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })} placeholder={t('tables.roomNamePlaceholder')} className="mt-1.5" data-testid="zone-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t('tables.width')}</Label><Input type="number" value={zoneForm.width} onChange={(e) => setZoneForm({ ...zoneForm, width: Number(e.target.value) })} className="mt-1.5" /></div>
              <div><Label>{t('tables.height')}</Label><Input type="number" value={zoneForm.height} onChange={(e) => setZoneForm({ ...zoneForm, height: Number(e.target.value) })} className="mt-1.5" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setZoneDialog(false)}>{t('common.cancel')}</Button><Button onClick={saveZone} data-testid="zone-save-button">{t('common.save')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change table */}
      <Dialog open={changeOpen} onOpenChange={setChangeOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t('tables.moveOrderTitle')}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-80 overflow-auto">
            {freeTables.length === 0 && <div className="text-sm text-muted-foreground">{t('tables.noFreeTables')}</div>}
            {freeTables.map((tbl) => (
              <button key={tbl.id} onClick={() => changeTable(tbl.id)} className="w-full h-12 rounded-xl border border-border hover:bg-muted flex items-center justify-between px-4 transition-colors">
                <span className="font-medium">{tbl.name}</span><span className="text-sm text-muted-foreground">{t('tables.seatsCount', { count: tbl.seats })}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
